import { useState } from 'react';
import {
  IconSearch,
  IconPlus,
  IconPlayerPlay,
  IconEdit,
  IconTrash,
  IconChartBar,
  IconTable,
  IconReport,
} from '@tabler/icons-react';
import { ActionIcon, Badge, Box, Button, Card, Group, Select, Stack, Table, Text, TextInput, Title } from '@/components/ui';
import { mockReports, Report } from '../mockData/analytics';

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const getTypeIcon = (type: Report['type']) => {
  switch (type) {
    case 'chart': return IconChartBar;
    case 'table': return IconTable;
    case 'summary': return IconReport;
    default: return IconReport;
  }
};

const getStatusColor = (status: Report['status']) => {
  switch (status) {
    case 'active': return 'green';
    case 'paused': return 'yellow';
    case 'draft': return 'gray';
    default: return 'gray';
  }
};

export default function ReportsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  const statuses = ['active', 'paused', 'draft'];
  const types = ['chart', 'table', 'summary'];

  const filteredReports = mockReports.filter(report => {
    const matchesSearch = report.name.toLowerCase().includes(search.toLowerCase()) ||
                          report.description.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || report.status === statusFilter;
    const matchesType = !typeFilter || report.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const activeCount = mockReports.filter(r => r.status === 'active').length;
  const scheduledCount = mockReports.filter(r => r.schedule).length;

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Reports</Title>
          <Text c="dimmed" size="sm">{filteredReports.length} reports</Text>
        </Box>
        <Button leftSection={<IconPlus size={16} />}>Create Report</Button>
      </Group>

      {/* Summary Cards */}
      <Group>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Total Reports</Text>
          <Text size="xl" fw={700}>{mockReports.length}</Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Active</Text>
          <Text size="xl" fw={700} c="green">{activeCount}</Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Scheduled</Text>
          <Text size="xl" fw={700} c="blue">{scheduledCount}</Text>
        </Card>
      </Group>

      {/* Filters */}
      <Card>
        <Group>
          <TextInput
            placeholder="Search reports..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Status"
            data={statuses.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={150}
          />
          <Select
            placeholder="Type"
            data={types.map(t => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))}
            value={typeFilter}
            onChange={setTypeFilter}
            clearable
            w={150}
          />
        </Group>
      </Card>

      {/* Reports Table */}
      <Card>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Report</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Schedule</Table.Th>
              <Table.Th>Last Run</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th ta="right">Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredReports.map(report => {
              const TypeIcon = getTypeIcon(report.type);
              return (
                <Table.Tr key={report.id}>
                  <Table.Td>
                    <Group gap="sm">
                      <Box
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 6,
                          backgroundColor: 'var(--mantine-color-blue-light)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <TypeIcon size={16} color="var(--mantine-color-blue-filled)" />
                      </Box>
                      <Box>
                        <Text size="sm" fw={500}>{report.name}</Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>{report.description}</Text>
                      </Box>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light">{report.type}</Badge>
                  </Table.Td>
                  <Table.Td>
                    {report.schedule ? (
                      <Badge size="sm" variant="light" color="blue">{report.schedule}</Badge>
                    ) : (
                      <Text size="sm" c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{formatDate(report.lastRunAt)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" color={getStatusColor(report.status)}>{report.status}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end">
                      <ActionIcon variant="light" size="sm" title="Run">
                        <IconPlayerPlay size={14} />
                      </ActionIcon>
                      <ActionIcon variant="light" size="sm" title="Edit">
                        <IconEdit size={14} />
                      </ActionIcon>
                      <ActionIcon variant="light" color="red" size="sm" title="Delete">
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}
